import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
} from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { rosbagPlayerFields } from 'src/app/models_new/config/rosbag-player-fields';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics-service.service';
import { Field } from '../../../models_new/classes/field';
import { RosbagPlayerService } from '../../../services/rosbag/rosbag-player.service';
import { IMovmentBoxOption } from 'src/app/models_new/types/3dview/rosbag-player-types';

export enum PlayerMode {
  DEFAULT = 'default',
  THEATER = 'theater',
  FULLSCREEN = 'fullscreen',
}

@Component({
  selector: 'app-rosbag-player',
  templateUrl: './rosbag-player.component.html',
  styleUrls: ['./rosbag-player.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RosbagPlayerComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  @HostBinding('style.--rosbag-player-progress.%')
  loadingProgress: number | null;

  @Input()
  disabled: boolean = false;

  @Input() url: string;

  @Input() playerMode: PlayerMode = PlayerMode.DEFAULT;
  @Output() playerModeEvent?: EventEmitter<PlayerMode> = new EventEmitter();
  modeEnum = PlayerMode;

  destroy$ = new ReplaySubject<boolean>(1);

  private updateSlider = true;
  private emitSeekingStart = true;

  speedOptions = [0.25, 0.5, 1, 2, 4];

  @Input() movementBoxOptions: IMovmentBoxOption[] = null;
  movementBoxSelected: IMovmentBoxOption[] | null = null;

  rosbagPlayerFields = rosbagPlayerFields(this.speedOptions);

  value = 0;

  @Output() selectedMovementBox = new EventEmitter<IMovmentBoxOption[]>();
  @Input()
  enableMovementBoxButton = false;

  constructor(
    private gas: GoogleAnalyticsService,
    public player: RosbagPlayerService
  ) {
    this.player.playerEvents$.pipe(takeUntil(this.destroy$)).subscribe((e) => {
      if (e.type === 'stop') {
        this.value = 0; // Reset slider on stop
      } else if (e.type === 'silderupdate') {
        if (this.updateSlider) {
          this.value = e.data.keyframeIndex; // Reset slider on stop
        }
      }
    });

    this.destroy$.pipe(take(1)).subscribe((_value: boolean) => {
      this.player.clear();
    });
  }

  ngOnInit(): void {
    // Report loading progress
    this.player.buffer.readProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((v) => {
        this.loadingProgress = v;
      });

    this.getField('playback_speed')
      .formControl.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((value) => this.player.setPlaybackSpeed(value));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.url?.currentValue) {
      this.player.load(changes.url.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  play(): void {
    this.player.play();
    this.gas.addEvent(
      'Simulation Details - MyRobot.cloud	- Video - Play Button - Click',
      null,
      this.gas.getLastParam()
    );
  }

  pause(): void {
    this.player.pause();
    this.gas.addEvent(
      'Simulation Details - MyRobot.cloud	Video - Pause Button - Click',
      null,
      this.gas.getLastParam()
    );
  }

  stop(): void {
    this.player.stop();
    this.gas.addEvent(
      'Simulation Details - MyRobot.cloud	Video - Stop Button - Click',
      null,
      this.gas.getLastParam()
    );
  }

  // Contex is MatSlider not RosbagPlayerComponent! "this.player" is undefined
  formatLabel(value: number): number | string {
    if (typeof this.player === 'undefined') {
      return '';
    }

    return this.player.getKeyframeDisplayTime(value);
  }

  sliderChange(_event: any): void {
    this.updateSlider = true;

    this.player.emitSeekStopEvent();
    this.emitSeekingStart = true;
  }

  disableSliderUpdate(event: any): void {
    if (this.emitSeekingStart) {
      this.player.emitSeekStartEvent();
      this.emitSeekingStart = false;
    }

    // const temp = this.player.currentTime;
    this.updateSlider = false;
    this.player.seekToKeyframe(event.value);
  }

  getField(_id: string): Field {
    return this.rosbagPlayerFields.find((f) => f.id);
  }

  public togglePlayerMode(mode: PlayerMode): void {
    let outputMode = PlayerMode.DEFAULT;
    if (
      mode === PlayerMode.FULLSCREEN &&
      this.playerMode !== PlayerMode.FULLSCREEN
    )
      outputMode = PlayerMode.FULLSCREEN;
    if (mode === PlayerMode.THEATER && this.playerMode !== PlayerMode.THEATER)
      outputMode = PlayerMode.THEATER;
    this.playerModeEvent.emit(outputMode);
  }

  setPlaybackSpeed(speed: number): void {
    this.rosbagPlayerFields[0].formControl.setValue(speed);
  }

  setMovementBox(clear?): void {
    if (clear) {
      this.movementBoxOptions.forEach((option) => (option.isVisible = false));
      this.selectedMovementBox.emit((this.movementBoxSelected = null));
    } else
      this.selectedMovementBox.emit(
        (this.movementBoxSelected = this.movementBoxOptions.filter(
          (option) => option.isVisible
        ))
      );
  }
}
